/**
 * Pure tri-state "select all" math, shared by {@link useTriStateMasterCheckbox}
 * (a single master over a filtered view) and by any caller that needs the same
 * semantics for MANY scopes at once — e.g. a grouped multi-select rendering one
 * tri-state header per group, where the Rules of Hooks forbid calling the hook
 * in a loop. Extracting the logic here keeps ONE implementation of the
 * add-whole-scope / remove-whole-scope behavior rather than two copies.
 */

/** The three visual states of a tri-state master checkbox. */
export interface MasterCheckboxState {
  /** Every scoped value is currently selected. */
  readonly checked: boolean;
  /** Some — but not all — scoped values are selected. */
  readonly indeterminate: boolean;
  /** Whether toggling is meaningful — false when the scope is empty. */
  readonly hasItems: boolean;
}

/**
 * Compute the tri-state of a master checkbox over `scopedValues` given the full
 * `selected` set (which may include values outside the scope).
 */
export function computeMasterCheckboxState<T>(
  scopedValues: readonly T[],
  selected: readonly T[],
): MasterCheckboxState {
  if (scopedValues.length === 0) {
    return { checked: false, indeterminate: false, hasItems: false };
  }
  const selectedSet = new Set(selected);
  let count = 0;
  for (const value of scopedValues) {
    if (selectedSet.has(value)) count += 1;
  }
  return {
    checked: count === scopedValues.length,
    indeterminate: count > 0 && count < scopedValues.length,
    hasItems: true,
  };
}

/**
 * Toggle a whole scope against the current selection, preserving values outside
 * the scope. When every scoped value is already selected the scope is removed;
 * otherwise the whole scope is added. Returns a fresh de-duped array.
 */
export function toggleScopedSelection<T>(
  scopedValues: readonly T[],
  selected: readonly T[],
): T[] {
  const { checked } = computeMasterCheckboxState(scopedValues, selected);
  const next = new Set(selected);
  if (checked) {
    for (const value of scopedValues) next.delete(value);
  } else {
    for (const value of scopedValues) next.add(value);
  }
  return Array.from(next);
}
